import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Trash, User, PencilSimple, Crown, Key } from "@phosphor-icons/react";

const fmt = (iso) => new Date(iso).toLocaleDateString([], { dateStyle: "medium" });

const SECTIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "materials", label: "Raw Materials" },
  { key: "production", label: "Production" },
  { key: "production-plans", label: "Production Plans" },
  { key: "suppliers", label: "Suppliers" },
  { key: "purchase-orders", label: "Purchase Orders" },
  { key: "dispatch", label: "Dispatch & Revenue" },
  { key: "admins", label: "Admins" },
];
const emptyPerms = () => ({ ...SECTIONS.reduce((a, s) => ({ ...a, [s.key]: "none" }), {}), "plan-edit": "none" });
const LEVEL_STYLE = { edit: "bg-[#16A34A] text-white", view: "bg-[#0891B2] text-white", none: "bg-zinc-200 text-zinc-500" };

function PermissionGrid({ perms, setPerms }) {
  return (
    <div className="space-y-2" data-testid="permission-grid">
      <Label className="text-xs font-bold uppercase tracking-wider">Section Access</Label>
      <div className="border border-zinc-200 divide-y divide-zinc-100">
        {SECTIONS.map((s) => (
          <div key={s.key} className="flex items-center justify-between px-3 py-2">
            <span className="text-sm text-zinc-700">{s.label}</span>
            <Select value={perms[s.key]} onValueChange={(v) => setPerms({ ...perms, [s.key]: v })}>
              <SelectTrigger data-testid={`perm-select-${s.key}`} className="rounded-none border-zinc-300 w-32 h-8 text-xs focus:ring-2 focus:ring-primary"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="none">No access</SelectItem>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <Label className="text-xs font-bold uppercase tracking-wider pt-2 block">Action Permissions</Label>
      <div className="border border-zinc-200">
        <div className="flex items-center justify-between px-3 py-2">
          <div>
            <span className="text-sm text-zinc-700">Edit Production Plans</span>
            <p className="text-[11px] text-zinc-400">Correct material &amp; quantity on plan rows with no activity yet</p>
          </div>
          <Select value={perms["plan-edit"] || "none"} onValueChange={(v) => setPerms({ ...perms, "plan-edit": v })}>
            <SelectTrigger data-testid="perm-select-plan-edit" className="rounded-none border-zinc-300 w-32 h-8 text-xs focus:ring-2 focus:ring-primary"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="none">Not allowed</SelectItem>
              <SelectItem value="edit">Allowed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default function Admins() {
  const { user, canManageAdmins } = useAuth();
  const confirm = useConfirm();
  const canManage = canManageAdmins();
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [perms, setPerms] = useState(emptyPerms());
  const [saving, setSaving] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = () => api.get("/admins").then((r) => setAdmins(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditId(null);
    setForm({ name: "", email: "", password: "" });
    setPerms(emptyPerms());
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditId(a.id);
    setForm({ name: a.name, email: a.email, password: "" });
    setPerms({ ...emptyPerms(), ...(a.permissions || {}) });
    setOpen(true);
  };

  const save = async () => {
    if (editId) {
      if (!form.name.trim()) return toast.error("Name is required");
      setSaving(true);
      try {
        await api.put(`/admins/${editId}`, { name: form.name.trim(), permissions: perms });
        toast.success("Access updated");
        setOpen(false);
        load();
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
      finally { setSaving(false); }
      return;
    }
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6)
      return toast.error("Name, email and a password (min 6 chars) are required");
    setSaving(true);
    try {
      await api.post("/admins", { ...form, permissions: perms });
      toast.success("Person added");
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!(await confirm({ title: "Remove Person", description: `Remove ${a.name} (${a.email})? They will lose all access.`, confirmText: "Remove" }))) return;
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success("Person removed");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const doReset = async () => {
    if (resetPw.length < 6) return toast.error("Password must be at least 6 characters");
    setResetting(true);
    try {
      await api.post(`/admins/${resetUser.id}/reset-password`, { password: resetPw });
      toast.success(`Password reset for ${resetUser.name}`);
      setResetUser(null);
      setResetPw("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  return (
    <>
      <PageHeader title="Admins" subtitle="Access Management">
        {canManage && (
          <Button onClick={openNew} data-testid="add-admin-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider transition-colors duration-100">
            <UserPlus size={16} weight="bold" className="mr-1" /> Add Person
          </Button>
        )}
      </PageHeader>

      <div className="p-4 sm:p-8">
        <div className="bg-white border border-zinc-300 divide-y divide-zinc-200" data-testid="admins-list">
          {admins.map((a) => (
            <div key={a.id} className="flex items-start justify-between px-6 py-4" data-testid={`admin-row-${a.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center shrink-0">
                  {a.is_owner ? <Crown size={18} weight="fill" className="text-primary" /> : <User size={18} weight="bold" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-900">
                    {a.name}
                    {a.id === user?.id && <span className="text-[10px] uppercase text-primary font-bold ml-1">You</span>}
                    {a.is_owner && <span className="text-[10px] uppercase text-zinc-500 font-bold ml-1">Owner · Full access</span>}
                  </p>
                  <p className="text-xs text-zinc-500 mb-2">{a.email}</p>
                  {!a.is_owner && (
                    <div className="flex flex-wrap gap-1" data-testid={`admin-perms-${a.id}`}>
                      {SECTIONS.filter((s) => (a.permissions || {})[s.key] && a.permissions[s.key] !== "none").map((s) => (
                        <span key={s.key} className={`text-[10px] uppercase font-bold px-1.5 py-0.5 ${LEVEL_STYLE[a.permissions[s.key]]}`}>
                          {s.label}: {a.permissions[s.key]}
                        </span>
                      ))}
                      {SECTIONS.every((s) => !(a.permissions || {})[s.key] || a.permissions[s.key] === "none") && (
                        <span className="text-[11px] text-zinc-400">No sections granted yet</span>
                      )}
                    </div>
                  )}
                  {!a.is_owner && a.last_access_change && (
                    <p className="text-[11px] text-zinc-400 mt-1.5" data-testid={`admin-audit-${a.id}`}>
                      {a.last_access_change.action} by {a.last_access_change.by_name} · {fmt(a.last_access_change.at)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <p className="text-xs text-zinc-400 hidden sm:block">Added {fmt(a.created_at)}</p>
                {canManage && !a.is_owner && (
                  <button onClick={() => openEdit(a)} data-testid={`edit-admin-${a.id}`} title="Edit access" className="text-zinc-500 hover:text-primary p-2 transition-colors duration-100"><PencilSimple size={16} weight="bold" /></button>
                )}
                {canManage && !a.is_owner && (
                  <button onClick={() => { setResetUser(a); setResetPw(""); }} data-testid={`reset-admin-${a.id}`} title="Reset password" className="text-zinc-500 hover:text-[#0891B2] p-2 transition-colors duration-100"><Key size={16} weight="bold" /></button>
                )}
                {canManage && !a.is_owner && a.id !== user?.id && (
                  <button onClick={() => remove(a)} data-testid={`delete-admin-${a.id}`} title="Remove" className="text-[#DC2626] hover:bg-[#DC2626] hover:text-white p-2 transition-colors duration-100"><Trash size={16} weight="bold" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editId ? "Edit Access" : "New Person"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Full Name</Label>
              <Input data-testid="admin-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            {!editId && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
                  <Input type="email" data-testid="admin-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider">Temporary Password</Label>
                  <Input type="password" data-testid="admin-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
                  <p className="text-[11px] text-zinc-400">They will still verify via an email OTP on every login.</p>
                </div>
              </>
            )}
            {editId && <p className="text-xs text-zinc-500 border-l-2 border-primary pl-2">{form.email}</p>}
            <PermissionGrid perms={perms} setPerms={setPerms} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="admin-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : editId ? "Update Access" : "Create Person"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setResetPw(""); } }}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="reset-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-zinc-600">Set a new temporary password for <span className="font-semibold">{resetUser?.name}</span> ({resetUser?.email}). They'll still verify by email OTP on login.</p>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">New Password</Label>
              <Input type="password" data-testid="reset-password-input" value={resetPw} onChange={(e) => setResetPw(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetUser(null); setResetPw(""); }} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={doReset} disabled={resetting} data-testid="reset-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{resetting ? "Resetting…" : "Reset Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
